// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, googleProvider } from '../firebase';
import {
    signInWithPopup,
    onAuthStateChanged,
    signOut as firebaseSignOut
} from 'firebase/auth';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp,
    increment,
    collection,
    query,
    orderBy,
    limit,
    getDocs
} from 'firebase/firestore';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);

            if (user) {
                setUser(user);
                await loadUserData(user.uid);
            } else {
                setUser(null);
                setUserData(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const loadUserData = async (uid) => {
        try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            const today = new Date().toDateString();

            if (userDoc.exists()) {
                const data = userDoc.data();

                // Ako je novi dan, sačuvaj prethodnu dnevnu statistiku
                if (data.lastPlayDate !== today && data.lastPlayDate) {
                    await saveDailyHistory(uid, data.lastPlayDate, data.dailyScore || 0);
                }

                // Reset daily data if new day
                if (data.lastPlayDate !== today) {
                    const updateData = {
                        dailyAttempts: 100,
                        dailyScore: 0,
                        lastPlayDate: today
                    };

                    await updateDoc(doc(db, 'users', uid), updateData);

                    // Update local data
                    data.dailyAttempts = 100;
                    data.dailyScore = 0;
                    data.lastPlayDate = today;
                }

                setUserData(data);
                return data;
            }
            return null;
        } catch (error) {
            console.error("Error loading user data:", error);
            return null;
        }
    };

    // Formatuje datum za korišćenje kao ID dokumenta
    const formatDateForId = (dateString) => {
        // Pretvara "Sat Jun 28 2025" u "2025-06-28"
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    // Funkcija za čuvanje/ažuriranje dnevne istorije
    const saveDailyHistory = async (uid, date, score, additionalData = {}) => {
        try {
            const dateId = formatDateForId(date);
            const dailyHistoryDocRef = doc(db, 'users', uid, 'dailyHistory', dateId);

            // Proveri da li dokument već postoji
            const existingDoc = await getDoc(dailyHistoryDocRef);

            const historyData = {
                date: date,
                score: score,
                lastUpdated: serverTimestamp(),
                ...additionalData
            };

            if (existingDoc.exists()) {
                // Ažuriraj postojeći dokument
                const existingData = existingDoc.data();
                await updateDoc(dailyHistoryDocRef, {
                    ...historyData,
                    // Zadrži originalni timestamp kreiranja ako postoji
                    createdAt: existingData.createdAt || serverTimestamp(),
                    // Možete dodati logiku za kombinovanje podataka ako je potrebno
                    // npr. ako želite da sumiraš rezultate umesto prepisivanja:
                    // score: (existingData.score || 0) + score,
                });
                console.log(`Updated daily history: ${date} - ${score} points (was ${existingData.score})`);
            } else {
                // Kreiraj novi dokument
                await setDoc(dailyHistoryDocRef, {
                    ...historyData,
                    createdAt: serverTimestamp()
                });
                console.log(`Created daily history: ${date} - ${score} points`);
            }

        } catch (error) {
            console.error("Error saving daily history:", error);
        }
    };

    // Funkcija za dobijanje dnevne istorije
    const getDailyHistory = async (days = 30) => {
        if (!user) return [];

        try {
            const dailyHistoryRef = collection(db, 'users', user.uid, 'dailyHistory');
            const q = query(
                dailyHistoryRef,
                orderBy('date', 'desc'),
                limit(days)
            );

            const querySnapshot = await getDocs(q);
            const history = [];

            querySnapshot.forEach((doc) => {
                history.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            return history;
        } catch (error) {
            console.error("Error getting daily history:", error);
            return [];
        }
    };

    // Funkcija za dobijanje specifičnog dnevnog rekorda
    const getDayHistory = async (date) => {
        if (!user) return null;

        try {
            const dateId = formatDateForId(date);
            const dailyHistoryDocRef = doc(db, 'users', user.uid, 'dailyHistory', dateId);
            const docSnap = await getDoc(dailyHistoryDocRef);

            if (docSnap.exists()) {
                return {
                    id: docSnap.id,
                    ...docSnap.data()
                };
            }
            return null;
        } catch (error) {
            console.error("Error getting day history:", error);
            return null;
        }
    };

    // Funkcija za ažuriranje trenutnog dana (poziva se u toku dana)
    const updateTodayHistory = async (additionalScore = 0, additionalData = {}) => {
        if (!user) return;

        try {
            const today = new Date().toDateString();
            const currentScore = userData?.dailyScore || 0;

            await saveDailyHistory(user.uid, today, currentScore + additionalScore, {
                inProgress: true, // Označava da je dan još uvek u toku
                ...additionalData
            });
        } catch (error) {
            console.error("Error updating today's history:", error);
        }
    };

    const signInWithGoogle = async () => {
        try {
            setLoading(true);
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            // Check if user exists
            const userDoc = await getDoc(doc(db, 'users', user.uid));

            if (!userDoc.exists()) {
                // New user - check for referral
                const urlParams = new URLSearchParams(window.location.search);
                const referrerId = urlParams.get('ref');

                await setDoc(doc(db, 'users', user.uid), {
                    email: user.email,
                    name: user.displayName,
                    createdAt: serverTimestamp(),
                    dailyAttempts: 100,
                    bonusAttempts: 0,
                    dailyScore: 0,
                    totalScore: 0,
                    lastPlayDate: new Date().toDateString(),
                    referredBy: referrerId || null,
                    isAdmin: false
                });

                // Give referrer bonus
                if (referrerId) {
                    await updateDoc(doc(db, 'users', referrerId), {
                        bonusAttempts: increment(100)
                    });
                }
            }

            return user;
        } catch (error) {
            console.error("Login error:", error);
            setLoading(false);
            throw error;
        }
    };

    const signOut = async () => {
        try {
            // Pre izlaska, sačuvaj današnju statistiku
            if (user && userData) {
                const today = new Date().toDateString();
                await saveDailyHistory(user.uid, today, userData.dailyScore || 0, {
                    sessionEnded: serverTimestamp()
                });
            }

            await firebaseSignOut(auth);
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const updateUserData = async (updates) => {
        if (!user) return;

        try {
            await updateDoc(doc(db, 'users', user.uid), updates);
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const newData = userDoc.data();
            setUserData(newData);
            return newData;
        } catch (error) {
            console.error("Error updating user data:", error);
            throw error;
        }
    };

    const addScore = async (scoreToAdd) => {
        if (!user || !userData) return;

        try {
            const updates = {
                dailyScore: increment(scoreToAdd),
                totalScore: increment(scoreToAdd)
            };

            await updateDoc(doc(db, 'users', user.uid), updates);

            // Update local state
            setUserData(prev => ({
                ...prev,
                dailyScore: (prev.dailyScore || 0) + scoreToAdd,
                totalScore: (prev.totalScore || 0) + scoreToAdd
            }));

            // Ažuriraj i istoriju za današnji dan
            await updateTodayHistory();

            return { success: true };
        } catch (error) {
            console.error("Error adding score:", error);
            throw error;
        }
    };

    // Funkcija za dobijanje statistika
    const getStatistics = async () => {
        try {
            const history = await getDailyHistory(365); // Poslednji godina dana

            const stats = {
                totalDays: history.length,
                averageDaily: history.length > 0 ?
                    history.reduce((sum, day) => sum + day.score, 0) / history.length : 0,
                bestDay: history.length > 0 ?
                    Math.max(...history.map(day => day.score)) : 0,
                last7Days: history.slice(0, 7).reduce((sum, day) => sum + day.score, 0),
                last30Days: history.slice(0, 30).reduce((sum, day) => sum + day.score, 0),
                currentStreak: calculateStreak(history)
            };

            return stats;
        } catch (error) {
            console.error("Error getting statistics:", error);
            return null;
        }
    };

    // Pomoćna funkcija za računanje streaka
    const calculateStreak = (history) => {
        let streak = 0;
        const today = new Date().toDateString();

        // Proveri da li je igrao danas
        if (userData?.dailyScore > 0 ||
            (history.length > 0 && history[0].date === today)) {
            streak = 1;
        }

        // Broji unazad kroz istoriju
        for (let i = (history[0]?.date === today ? 1 : 0); i < history.length; i++) {
            const currentDate = new Date(history[i].date);
            const expectedDate = new Date();
            expectedDate.setDate(expectedDate.getDate() - (streak + 1));

            if (currentDate.toDateString() === expectedDate.toDateString() &&
                history[i].score > 0) {
                streak++;
            } else {
                break;
            }
        }

        return streak;
    };

    const value = {
        user,
        userData,
        loading,
        signInWithGoogle,
        signOut,
        updateUserData,
        addScore,
        getDailyHistory,        // Postojeća funkcija
        getDayHistory,          // Nova funkcija za specifičan dan
        updateTodayHistory,     // Nova funkcija za ažuriranje današnjeg dana
        getStatistics,
        reloadUserData: () => user && loadUserData(user.uid)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};